var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { Instant } from '../../utility/datetime';
import { formatTime } from '../../utility/formatters';
import Card from './card';
import Button from './button';
import IconButton from './button.icon';
import Icon from './icon';
import CInput from './input';
import { fetchShopOpsNotes, createShopOpsNote, updateShopOpsNoteCategory, deleteShopOpsNote, fetchUserPreferences, setShopOpsLockOpen, } from '../../http/shop-ops';
const CATEGORY_ROLLUP = {
    'focus_today': 'urgent',
    'waiting_parts': 'urgent',
    'follow_up': 'pending',
    'call_back': 'pending',
    'done': 'done',
};
const CATEGORY_LABEL = {
    'focus_today': 'Focus today',
    'follow_up': 'Follow up',
    'waiting_parts': 'Waiting on parts',
    'call_back': 'Call back',
    'done': 'Done',
};
const CATEGORIES = ['focus_today', 'follow_up', 'waiting_parts', 'call_back', 'done'];
const ROLLUP_COLOR = {
    'urgent': '#dc3545',
    'pending': '#fd7e14',
    'done': '#198754',
};
const ROLLUP_BG = {
    'urgent': '#fde8ea',
    'pending': '#fff1e6',
    'done': '#e6f4ea',
};
const summarizeByCategory = (notes) => {
    const s = { 'focus_today': 0, 'follow_up': 0, 'waiting_parts': 0, 'call_back': 0, 'done': 0 };
    for (const n of notes)
        s[n.category]++;
    return s;
};
export default (props) => {
    const [notes, setNotes] = React.useState(undefined);
    const [open, setOpen] = React.useState(false);
    const [lockOpen, setLockOpen] = React.useState(false);
    const [addCategory, setAddCategory] = React.useState('focus_today');
    const [addText, setAddText] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const loadNotes = React.useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const data = yield fetchShopOpsNotes(props.merchantId);
            setNotes(data);
        }
        catch (_a) {
            setNotes([]);
        }
    }), [props.merchantId]);
    React.useEffect(() => {
        let cancelled = false;
        const init = () => __awaiter(void 0, void 0, void 0, function* () {
            setNotes(null);
            try {
                const [notesData, prefs] = yield Promise.all([
                    fetchShopOpsNotes(props.merchantId),
                    fetchUserPreferences(),
                ]);
                if (cancelled)
                    return;
                setNotes(notesData);
                if (prefs.shop_ops_lock_open) {
                    setLockOpen(true);
                    setOpen(true);
                }
            }
            catch (_a) {
                if (!cancelled)
                    setNotes([]);
            }
        });
        init();
        const interval = setInterval(loadNotes, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(interval); };
    }, [props.merchantId, loadNotes]);
    const onAdd = () => __awaiter(void 0, void 0, void 0, function* () {
        const text = addText.trim();
        if (!text || saving)
            return;
        setSaving(true);
        try {
            yield createShopOpsNote(props.merchantId, addCategory, text);
            setAddText('');
            yield loadNotes();
        }
        finally {
            setSaving(false);
        }
    });
    const onChangeCategory = (noteId, category) => __awaiter(void 0, void 0, void 0, function* () {
        yield updateShopOpsNoteCategory(props.merchantId, noteId, category);
        yield loadNotes();
    });
    const onDelete = (noteId) => __awaiter(void 0, void 0, void 0, function* () {
        yield deleteShopOpsNote(props.merchantId, noteId);
        yield loadNotes();
    });
    const onToggleLock = () => __awaiter(void 0, void 0, void 0, function* () {
        const next = !lockOpen;
        setLockOpen(next);
        if (next)
            setOpen(true);
        try {
            yield setShopOpsLockOpen(next);
        }
        catch (_a) {
            setLockOpen(!next);
        }
    });
    const summary = summarizeByCategory(notes || []);
    const pill = (category) => {
        const count = summary[category];
        if (count === 0)
            return null;
        const rollup = CATEGORY_ROLLUP[category];
        return _jsxs("span", { style: {
                'backgroundColor': ROLLUP_BG[rollup],
                'color': ROLLUP_COLOR[rollup],
                'padding': '2px 8px',
                'borderRadius': '12px',
                'fontSize': '11px',
                'fontWeight': 600,
                'marginLeft': '6px',
            }, children: [count, " ", CATEGORY_LABEL[category]] }, category);
    };
    if (notes === undefined)
        return null;
    if (!open) {
        return _jsx("div", { style: { 'marginBottom': '2px' }, children: _jsx(Button, { color: "primary", w100: true, onClick: () => setOpen(true), children: _jsxs("span", { style: { 'display': 'flex', 'alignItems': 'center', 'justifyContent': 'space-between', 'width': '100%' }, children: [_jsxs("span", { style: { 'fontWeight': 700 }, children: [_jsx(Icon, { size: "inherit", children: "dashboard" }), " Quick Notes"] }), _jsxs("span", { children: [CATEGORIES.map(pill), _jsx(Icon, { size: "inherit", children: "expand_more" })] })] }) }) });
    }
    return _jsx("div", { style: { 'marginBottom': '2px' }, children: _jsxs(Card, { title: "Quick Notes", icon: "dashboard", action: _jsxs(_Fragment, { children: [_jsx(IconButton, { color: lockOpen ? 'primary' : 'transparent', onClick: onToggleLock, title: lockOpen ? 'Unlock' : 'Lock open', children: lockOpen ? 'lock' : 'lock_open' }), !lockOpen && _jsx(IconButton, { color: "transparent", onClick: () => setOpen(false), title: "Collapse", children: "expand_less" })] }), children: [_jsxs("div", { style: { 'display': 'flex', 'gap': '8px', 'alignItems': 'center', 'marginBottom': '12px' }, children: [_jsx("select", { className: "form-select form-select-sm", value: addCategory, onChange: (e) => setAddCategory(e.target.value), style: { 'width': '180px', 'flexShrink': 0 }, children: CATEGORIES.map((c) => _jsx("option", { value: c, children: CATEGORY_LABEL[c] }, c)) }), _jsx("div", { style: { 'flex': 1 }, onKeyDown: (e) => { if (e.key === 'Enter') {
                                e.preventDefault();
                                onAdd();
                            } }, children: _jsx(CInput, { type: "text", value: addText, onChange: setAddText, inline: true, small: true, children: "Add a quick note..." }) }), _jsx(Button, { color: "primary", onClick: onAdd, disabled: saving || !addText.trim(), children: "Add" })] }), notes === null && _jsx("div", { className: "text-muted text-center py-2", children: "Loading..." }), notes && notes.length === 0 && _jsx("div", { className: "text-muted text-center py-2", children: "No items on the board." }), notes && notes.length > 0 && _jsx("div", { children: notes.map((note) => {
                        const rollup = CATEGORY_ROLLUP[note.category];
                        const isDone = note.category === 'done';
                        return _jsxs("div", { className: "shop-ops-row", style: {
                                'display': 'flex',
                                'alignItems': 'center',
                                'gap': '10px',
                                'padding': '6px 4px',
                                'borderBottom': '1px solid #f0f0f0',
                            }, children: [_jsx("span", { style: {
                                        'display': 'inline-block',
                                        'width': '8px',
                                        'height': '8px',
                                        'borderRadius': '50%',
                                        'backgroundColor': ROLLUP_COLOR[rollup],
                                        'flexShrink': 0,
                                    } }), _jsx("span", { style: {
                                        'flex': 1,
                                        'textDecoration': isDone ? 'line-through' : 'none',
                                        'color': isDone ? '#9ca3af' : 'inherit',
                                    }, children: note.text }), _jsx("select", { className: "form-select form-select-sm", value: note.category, onChange: (e) => onChangeCategory(note.id, e.target.value), style: {
                                        'width': '150px',
                                        'fontSize': '11px',
                                        'padding': '2px 22px 2px 8px',
                                        'backgroundColor': ROLLUP_BG[rollup],
                                        'color': ROLLUP_COLOR[rollup],
                                        'border': 'none',
                                        'fontWeight': 600,
                                    }, children: CATEGORIES.map((c) => _jsx("option", { value: c, children: CATEGORY_LABEL[c] }, c)) }), _jsxs("span", { style: { 'color': '#9ca3af', 'fontSize': '12px', 'textAlign': 'right', 'whiteSpace': 'nowrap' }, children: [formatTime(Instant.parse(note.created_at)), !!note.created_by_name.trim() && _jsxs("span", { style: { 'marginLeft': '6px' }, children: ["\u00B7 ", note.created_by_name] })] }), _jsx(IconButton, { color: "transparent", onClick: () => onDelete(note.id), title: "Delete", children: "close" })] }, note.id);
                    }) })] }) });
};
